import { useMemo } from 'react'
import { VStack, UnorderedList, ListItem, Text } from '@chakra-ui/react'
import { useTranslation, Trans } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'

import { TEXT_STYLE, LIST_STYLE } from '@/features/landing/constants/FAQs'
import { Item as FAQItem } from '@/features/landing/components/FAQs'
import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'
import type { TextWithImageProps } from '@/features/services/templates/TextWithImage'
import { strongStyle } from '@/features/landing/components/Carousel/Contents/style'

import DesktopBgImage from '@/images/services/implementation/banner-desktop.jpg'
import MobileBgImage from '@/images/services/implementation/banner-mobile.jpg'
import InfoImage from '@/images/services/implementation/info.png'
import FloatImage from '@/images/services/implementation/floating.jpg'

import type { WidgetProps, IconTextProps } from '../types'
import { TARGET_TEXT_STYLE } from './style'

export const useMeta = (): PageMeta => {
  const { t } = useTranslation('services')

  return {
    title: t('implementation.meta.title'),
    description: t('implementation.meta.description'),
    og: {
      title: t('implementation.meta.og.title'),
      description: t('implementation.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('implementation.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('implementation.title'),
      subtitle: (
        <Text as="span" sx={{ lineHeight: 'inherit' }}>
          <Trans t={t} i18nKey="implementation.subtitle">
            ดำเนินการให้ธุรกิจสอดคล้อง PDPA
            <br />
            จากทีมนักกฏหมายผู้เชี่ยวชาญ
          </Trans>
        </Text>
      ),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'secondary.650',
      subtitleColor: 'gray.900',
      colorSchemePrimary: 'secondary',
      colorSchemeSecondary: 'secondary',
      bgColor: 'gray.25',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'Implementation',
            action: 'Click get quote',
          },
        })
      },
      onClickGetHandout: (): void => {
        logEvent({
          ga: {
            category: 'Implementation',
            action: 'Click data sheet',
            label: 'header',
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('implementation.serviceInfo.title'),
      paragraph: t('implementation.serviceInfo.paragraph'),
      image: InfoImage,
    }),
    [language],
  )
}

export const useAspectData = (): IconTextProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/implementation/aspect-1.svg',
        text: t('implementation.aspects.contents.0'),
      },
      {
        image: '/images/services/implementation/aspect-2.svg',
        text: t('implementation.aspects.contents.1'),
      },
      {
        image: '/images/services/implementation/aspect-3.svg',
        text: t('implementation.aspects.contents.2'),
      },
      {
        image: '/images/services/implementation/aspect-4.svg',
        text: t('implementation.aspects.contents.3'),
      },
      {
        image: '/images/services/implementation/aspect-5.svg',
        text: t('implementation.aspects.contents.4'),
      },
    ],
    [language],
  )
}

export const useExampleData = (): WidgetProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      {
        image: '/images/services/implementation/example-1.svg',
        text: t('implementation.example.contents.0'),
      },
      {
        image: '/images/services/implementation/example-2.svg',
        text: t('implementation.example.contents.1'),
      },
      {
        image: '/images/services/implementation/example-3.svg',
        text: t('implementation.example.contents.2'),
      },
      {
        image: '/images/services/implementation/example-4.svg',
        text: t('implementation.example.contents.3'),
      },
    ],
    [language],
  )
}

export const useTargetData = (): TextWithImageProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('implementation.target.title'),
      paragraph: (
        <Text sx={TARGET_TEXT_STYLE}>
          {t('implementation.target.paragraph')}
        </Text>
      ),
      image: FloatImage,
      imageRatio: 678 / 364,
    }),
    [language],
  )
}

export const useFAQsData = (): FAQItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('faqs')

  return useMemo(
    () => [
      {
        title: t('FAQs.0.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.0.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.1.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.1.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.2.question'),
        content: (
          <VStack sx={{ alignItems: 'stretch' }}>
            <Text sx={TEXT_STYLE}>{t('FAQs.2.answer.paragraph')}</Text>
            <UnorderedList sx={LIST_STYLE}>
              <ListItem>{t('FAQs.2.answer.list.0')}</ListItem>
              <ListItem>{t('FAQs.2.answer.list.1')}</ListItem>
              <ListItem>{t('FAQs.2.answer.list.2')}</ListItem>
            </UnorderedList>
          </VStack>
        ),
      },
      {
        title: t('FAQs.5.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.5.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.7.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.7.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.8.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.8.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.9.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.9.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.10.question'),
        content: (
          <VStack spacing="4" sx={{ alignItems: 'stretch' }}>
            <Text sx={TEXT_STYLE}>{t('FAQs.10.answer.paragraph.0')}</Text>
            <UnorderedList sx={LIST_STYLE}>
              <ListItem>{t('FAQs.10.answer.list.0')}</ListItem>
              <ListItem>{t('FAQs.10.answer.list.1')}</ListItem>
              <ListItem>{t('FAQs.10.answer.list.2')}</ListItem>
            </UnorderedList>
            <Text sx={TEXT_STYLE}>{t('FAQs.10.answer.paragraph.1')}</Text>
          </VStack>
        ),
      },
      {
        title: t('FAQs.11.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.11.answer.paragraph')}</Text>,
      },
    ],
    [language],
  )
}

export const usePricingData = (): React.ReactNode => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(() => t('implementation.pricing.contents.0'), [language])
}

export const useWhyUsData = (): React.ReactNode[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => [
      <Trans key="1" t={t} i18nKey="implementation.whyUs.contents.0">
        ไม่แน่ใจว่าต้องทำอะไรบ้างเพื่อให้ธุรกิจสอดคล้องกับกฎหมาย
        <Text as="strong" sx={strongStyle}>
          PDPA
        </Text>
      </Trans>,
      <Trans key="2" t={t} i18nKey="implementation.whyUs.contents.1">
        ไม่แน่ใจว่ารายการที่ทำไปแล้วถูกต้องตามที่กฏหมายกำหนดหรือไม่
      </Trans>,
      <Trans key="2" t={t} i18nKey="implementation.whyUs.contents.2">
        ต้องการทีมที่ทำให้ธุรกิจสอดคล้องกับ
        <Text as="strong" sx={strongStyle}>
          PDPA
        </Text>
      </Trans>,
    ],
    [language],
  )
}
